<template>
  <div class="voice-input-wrapper">
    <v-textarea
      v-if="multiline"
      v-model="internalValue"
      :label="label"
      :placeholder="placeholder"
      :rows="rows"
      auto-grow
      :autofocus="autofocus"
      :disabled="isListening"
      :hint="currentHint"
      :persistent-hint="isListening || isTranscribing"
      @input="onInput"
      @keydown.enter.exact="onEnterKey"
      class="voice-textarea"
    >
      <template v-slot:append>
        <v-btn
          icon
          small
          :color="buttonColor"
          @click="toggleListening"
          :disabled="isTranscribing"
          :title="buttonTitle"
          class="voice-btn"
          :class="{ 'recording-pulse': isListening }"
        >
          <v-icon>{{ buttonIcon }}</v-icon>
        </v-btn>
      </template>
    </v-textarea>
    <v-text-field
      v-else
      v-model="internalValue"
      :label="label"
      :placeholder="placeholder"
      :autofocus="autofocus"
      :disabled="isListening"
      :hint="currentHint"
      :persistent-hint="isListening || isTranscribing"
      @input="onInput"
      @keyup.enter="$emit('keyup-enter')"
      class="voice-textfield"
    >
      <template v-slot:append>
        <v-btn
          icon
          small
          :color="buttonColor"
          @click="toggleListening"
          :disabled="isTranscribing"
          :title="buttonTitle"
          class="voice-btn"
          :class="{ 'recording-pulse': isListening }"
        >
          <v-icon>{{ buttonIcon }}</v-icon>
        </v-btn>
      </template>
    </v-text-field>

    <div v-if="isListening" class="recording-bar">
      <div class="recording-bar-left">
        <span class="recording-dot"></span>
        <span class="recording-label">Recording</span>
        <span class="recording-time">{{ formattedElapsed }}</span>
      </div>
      <div class="recording-bar-right">
        <div
          v-if="autoSubmitOnSilence && silenceCountdown !== null"
          class="countdown-wrapper"
        >
          <svg
            class="countdown-ring"
            viewBox="0 0 28 28"
            width="28"
            height="28"
          >
            <circle
              cx="14"
              cy="14"
              r="12"
              fill="none"
              stroke="#e0e0e0"
              stroke-width="2.5"
            />
            <circle
              cx="14"
              cy="14"
              r="12"
              fill="none"
              stroke="#F59E0B"
              stroke-width="2.5"
              stroke-linecap="round"
              :stroke-dasharray="circumference"
              :stroke-dashoffset="countdownOffset"
              class="countdown-arc"
            />
          </svg>
          <span class="countdown-num">{{ silenceCountdown }}</span>
        </div>
        <v-btn
          small
          color="primary"
          depressed
          class="done-btn"
          @click="finishRecording"
        >
          <v-icon small left>mdi-check</v-icon>
          Done
        </v-btn>
      </div>
    </div>

    <div v-else-if="isTranscribing" class="processing-bar">
      <v-progress-circular
        indeterminate
        size="16"
        width="2"
        color="primary"
        class="mr-2"
      ></v-progress-circular>
      <span class="processing-label">Processing your audio...</span>
    </div>

    <div
      v-if="interimTranscript"
      class="interim-transcript text-caption grey--text"
    >
      {{ interimTranscript }}
    </div>
  </div>
</template>

<script>
// todo: This component is replacing r-textareas in lots of places in the app. See if we lost existing funcitonality, and existing styling.
export default {
  name: "VoiceInput",
  props: {
    value: {
      type: String,
      default: "",
    },
    label: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "",
    },
    hint: {
      type: String,
      default: "",
    },
    multiline: {
      type: Boolean,
      default: false,
    },
    rows: {
      type: Number,
      default: 3,
    },
    autofocus: {
      type: Boolean,
      default: false,
    },
    appendMode: {
      type: Boolean,
      default: true,
    },
    enterSubmits: {
      type: Boolean,
      default: false,
    },
    autoSubmitOnSilence: {
      type: Boolean,
      default: false,
    },
    silenceTimeoutMs: {
      type: Number,
      default: 3000,
    },
  },
  data() {
    return {
      internalValue: this.value || "",
      voiceBaseText: "",
      voiceSessionFinal: "",
      isListening: false,
      isTranscribing: false,
      useBrowserSpeech: false,
      recognition: null,
      interimTranscript: "",
      mediaRecorder: null,
      audioChunks: [],
      silenceTimer: null,
      lastFinalResultTime: 0,
      elapsedSeconds: 0,
      elapsedInterval: null,
      silenceCountdown: null,
      countdownInterval: null,
      countdownStartTime: null,
    };
  },
  computed: {
    currentHint() {
      if (this.isTranscribing) return "Processing your audio...";
      if (this.isListening) return "Speak now — press Done when finished";
      return this.hint;
    },
    buttonColor() {
      if (this.isTranscribing) return "warning";
      if (this.isListening) return "error";
      return "primary";
    },
    buttonIcon() {
      if (this.isTranscribing) return "mdi-loading mdi-spin";
      if (this.isListening) return "mdi-microphone";
      return "mdi-microphone";
    },
    buttonTitle() {
      if (this.isTranscribing) return "Processing...";
      if (this.isListening)
        return "Recording — click Done or this button to stop";
      if (!this.useBrowserSpeech)
        return "Speak to type (using cloud transcription)";
      return "Speak to type";
    },
    formattedElapsed() {
      var mins = Math.floor(this.elapsedSeconds / 60);
      var secs = this.elapsedSeconds % 60;
      return (
        (mins < 10 ? "0" : "") + mins + ":" + (secs < 10 ? "0" : "") + secs
      );
    },
    circumference() {
      return 2 * Math.PI * 12;
    },
    countdownOffset() {
      if (this.silenceCountdown === null) return 0;
      var totalSecs = this.silenceTimeoutMs / 1000;
      var fraction = this.silenceCountdown / totalSecs;
      return this.circumference * (1 - fraction);
    },
  },
  watch: {
    value(newVal) {
      if (newVal !== this.internalValue) {
        this.internalValue = newVal || "";
      }
    },
  },
  mounted() {
    this.checkSpeechSupport();
  },
  beforeDestroy() {
    this.clearSilenceTimer();
    this.stopElapsedTimer();
    this.stopCountdown();
    this.stopListening();
  },
  methods: {
    checkSpeechSupport() {
      var SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      this.useBrowserSpeech = !!SpeechRecognition;
      if (this.useBrowserSpeech) {
        this.initRecognition();
      }
    },
    initRecognition() {
      var SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      this.recognition = new SpeechRecognition();
      this.recognition.continuous = true;
      this.recognition.interimResults = true;
      this.recognition.lang = "en-US";
      var self = this;
      this.recognition.onresult = function(event) {
        // PR-VOICE-ANDROID-DEDUP (v2): rebuild the session transcript from the
        // FULL results array every event and stitch overlapping segments with
        // mergeFinals(). This is robust to the two Android/Samsung quirks that
        // caused duplicated words:
        //   1) re-segmentation - a final result is re-reported at a new index
        //      with earlier words repeated ("I led" then "I led the team")
        //   2) restart replay - after onend auto-restarts, the new session
        //      re-emits the just-finalized text at index 0
        // Because we SET (not append) and merge on whole-word overlap, both a
        // re-fire and a replay collapse instead of duplicating.
        var interim = "";
        var sessionFinal = "";
        for (var i = 0; i < event.results.length; i++) {
          var transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            sessionFinal = self.mergeFinals(sessionFinal, transcript);
          } else {
            interim += transcript;
          }
        }
        self.voiceSessionFinal = sessionFinal;
        var combined = self.mergeFinals(self.voiceBaseText, sessionFinal);
        var needsSpace =
          combined.length > 0 && interim && !combined.endsWith(" ");
        self.internalValue = combined + (needsSpace ? " " : "") + interim;
        self.$emit("input", self.internalValue);
        self.interimTranscript = interim;
        if (sessionFinal) {
          self.$emit("voice-result", sessionFinal);
          self.resetSilenceTimer();
        } else if (interim) {
          self.resetSilenceTimer();
        }
      };
      this.recognition.onerror = function(event) {
        console.error("Speech recognition error:", event.error);
        self.isListening = false;
        self.interimTranscript = "";
        self.stopElapsedTimer();
        self.stopCountdown();
        if (event.error === "not-allowed") {
          self.$emit(
            "voice-error",
            "Microphone access denied. Please allow microphone access and try again.",
          );
        } else if (event.error === "no-speech") {
          self.$emit("voice-error", "No speech detected. Please try again.");
        } else {
          self.$emit("voice-error", "Speech recognition error: " + event.error);
        }
      };
      this.recognition.onend = function() {
        if (self.isListening) {
          // PR-VOICE-ANDROID-DEDUP (v2): before auto-restart, commit this
          // session's finalized text into voiceBaseText via mergeFinals so a
          // restart-replay (the new session re-emitting these same words) is
          // stitched away instead of duplicated. Then clear the session buffer.
          if (self.voiceSessionFinal) {
            self.voiceBaseText = self.mergeFinals(
              self.voiceBaseText,
              self.voiceSessionFinal,
            );
          }
          self.voiceSessionFinal = "";
          try {
            self.recognition.start();
          } catch (e) {
            self.isListening = false;
            self.stopElapsedTimer();
            self.stopCountdown();
          }
        }
        self.interimTranscript = "";
      };
    },
    // PR-VOICE-ANDROID-DEDUP (v2): join two transcript fragments, collapsing a
    // whole-word overlap between the tail of `acc` and the head of `next`, so
    // Android's re-segmented / restart-replayed speech doesn't duplicate words.
    // Case-insensitive on the overlap test; conservative (whole-word boundaries
    // only) so normal speech is untouched.
    mergeFinals(acc, next) {
      acc = (acc || "").replace(/\s+$/, "");
      next = (next || "").replace(/^\s+/, "");
      if (!acc) return next;
      if (!next) return acc;
      var accWords = acc.split(/\s+/);
      var nextWords = next.split(/\s+/);
      var maxK = Math.min(accWords.length, nextWords.length);
      for (var k = maxK; k > 0; k--) {
        var aTail = accWords
          .slice(accWords.length - k)
          .join(" ")
          .toLowerCase();
        var bHead = nextWords
          .slice(0, k)
          .join(" ")
          .toLowerCase();
        if (aTail === bHead) {
          return accWords.concat(nextWords.slice(k)).join(" ");
        }
      }
      return acc + " " + next;
    },
    appendTranscript(text) {
      if (this.appendMode && this.internalValue) {
        var needsSpace =
          this.internalValue.trim().length > 0 &&
          !this.internalValue.endsWith(" ");
        this.internalValue =
          this.internalValue + (needsSpace ? " " : "") + text;
      } else {
        this.internalValue = text;
      }
      this.$emit("input", this.internalValue);
      this.$emit("voice-result", text);
    },
    startSilenceTimer() {
      if (!this.autoSubmitOnSilence) return;
      this.clearSilenceTimer();
      this.startCountdown();
      var self = this;
      this.silenceTimer = setTimeout(function() {
        if (self.isListening && self.internalValue.trim()) {
          self.stopListening();
          self.stopCountdown();
          self.$emit("voice-complete", self.internalValue);
        }
      }, this.silenceTimeoutMs);
    },
    resetSilenceTimer() {
      if (!this.autoSubmitOnSilence) return;
      this.stopCountdown();
      this.startSilenceTimer();
    },
    clearSilenceTimer() {
      if (this.silenceTimer) {
        clearTimeout(this.silenceTimer);
        this.silenceTimer = null;
      }
      this.stopCountdown();
    },
    startCountdown() {
      this.stopCountdown();
      var totalSecs = Math.ceil(this.silenceTimeoutMs / 1000);
      this.silenceCountdown = totalSecs;
      this.countdownStartTime = Date.now();
      var self = this;
      this.countdownInterval = setInterval(function() {
        var elapsed = Date.now() - self.countdownStartTime;
        var remaining = Math.ceil((self.silenceTimeoutMs - elapsed) / 1000);
        if (remaining <= 0) {
          self.silenceCountdown = 0;
          self.stopCountdown();
        } else {
          self.silenceCountdown = remaining;
        }
      }, 100);
    },
    stopCountdown() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
      this.silenceCountdown = null;
      this.countdownStartTime = null;
    },
    startElapsedTimer() {
      this.elapsedSeconds = 0;
      var self = this;
      this.elapsedInterval = setInterval(function() {
        self.elapsedSeconds++;
      }, 1000);
    },
    stopElapsedTimer() {
      if (this.elapsedInterval) {
        clearInterval(this.elapsedInterval);
        this.elapsedInterval = null;
      }
      this.elapsedSeconds = 0;
    },
    toggleListening() {
      if (this.isListening) {
        this.finishRecording();
      } else {
        this.startListening();
      }
    },
    finishRecording() {
      var hadContent = this.internalValue.trim().length > 0;
      this.stopListening();
      if (hadContent && this.autoSubmitOnSilence) {
        this.$emit("voice-complete", this.internalValue);
      }
    },
    startListening() {
      this.voiceBaseText = this.internalValue || "";
      // PR-VOICE-ANDROID-DEDUP (v2): the session transcript is rebuilt from the
      // full results array each event, so no index highwater mark is needed.
      this.voiceSessionFinal = "";
      if (this.useBrowserSpeech) {
        this.startBrowserSpeech();
      } else {
        this.startFallbackRecording();
      }
    },
    startBrowserSpeech() {
      if (!this.recognition) {
        this.initRecognition();
      }
      try {
        this.recognition.start();
        this.isListening = true;
        this.startElapsedTimer();
        this.$emit("voice-start");
        this.startSilenceTimer();
      } catch (e) {
        console.error("Failed to start recognition:", e);
        this.$emit(
          "voice-error",
          "Failed to start voice input. Please try again.",
        );
      }
    },
    startFallbackRecording() {
      var self = this;
      navigator.mediaDevices
        .getUserMedia({ audio: true })
        .then(function(stream) {
          self.audioChunks = [];
          self.mediaRecorder = new MediaRecorder(stream, {
            mimeType: self.getSupportedMimeType(),
          });
          self.mediaRecorder.ondataavailable = function(event) {
            if (event.data.size > 0) {
              self.audioChunks.push(event.data);
            }
          };
          self.mediaRecorder.onstop = function() {
            stream.getTracks().forEach(function(track) {
              track.stop();
            });
            self.transcribeAudio();
          };
          self.mediaRecorder.start(100);
          self.isListening = true;
          self.startElapsedTimer();
          self.$emit("voice-start");
        })
        .catch(function(e) {
          console.error("Failed to start recording:", e);
          self.$emit(
            "voice-error",
            "Microphone access denied or not available.",
          );
        });
    },
    getSupportedMimeType() {
      var types = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/ogg;codecs=opus",
        "audio/mp4",
      ];
      for (var i = 0; i < types.length; i++) {
        if (MediaRecorder.isTypeSupported(types[i])) {
          return types[i];
        }
      }
      return "audio/webm";
    },
    stopListening() {
      this.stopElapsedTimer();
      this.stopCountdown();
      if (this.useBrowserSpeech) {
        this.stopBrowserSpeech();
      } else {
        this.stopFallbackRecording();
      }
    },
    stopBrowserSpeech() {
      this.clearSilenceTimer();
      if (this.recognition) {
        try {
          this.recognition.stop();
        } catch (e) {
          // Ignore
        }
      }
      this.isListening = false;
      this.interimTranscript = "";
      this.$emit("voice-end");
    },
    stopFallbackRecording() {
      if (this.mediaRecorder && this.mediaRecorder.state === "recording") {
        this.mediaRecorder.stop();
      }
      this.isListening = false;
    },
    transcribeAudio() {
      if (this.audioChunks.length === 0) {
        this.$emit("voice-end");
        return;
      }
      this.isTranscribing = true;
      var self = this;
      var mimeType = this.getSupportedMimeType();
      var audioBlob = new Blob(this.audioChunks, { type: mimeType });
      this.blobToBase64(audioBlob)
        .then(function(base64Audio) {
          return fetch("/api/Transcribe", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ audio: base64Audio, mimeType: mimeType }),
          });
        })
        .then(function(response) {
          return response.json();
        })
        .then(function(result) {
          if (result.success && result.transcript) {
            self.appendTranscript(result.transcript);
          } else {
            console.error("Transcription failed:", result.msg);
            self.$emit("voice-error", result.msg || "Transcription failed");
          }
        })
        .catch(function(e) {
          console.error("Transcription error:", e);
          self.$emit("voice-error", "Failed to transcribe audio");
        })
        .finally(function() {
          self.isTranscribing = false;
          self.audioChunks = [];
          self.$emit("voice-end");
        });
    },
    blobToBase64(blob) {
      return new Promise(function(resolve, reject) {
        var reader = new FileReader();
        reader.onloadend = function() {
          var dataUrl = reader.result;
          var base64 = dataUrl.split(",")[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    },
    onInput(value) {
      this.$emit("input", value);
    },
    onEnterKey(event) {
      if (this.enterSubmits) {
        event.preventDefault();
        this.$emit("submit");
      }
    },
  },
};
</script>

<style scoped>
.voice-input-wrapper {
  position: relative;
}
.voice-btn {
  margin-top: -4px;
}
.recording-pulse {
  animation: pulse-ring 1.2s ease-in-out infinite;
}
@keyframes pulse-ring {
  0% {
    box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.5);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(244, 67, 54, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(244, 67, 54, 0);
  }
}
.recording-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff3e0;
  border: 1px solid #ffb74d;
  border-radius: 8px;
  padding: 6px 12px;
  margin-top: 4px;
}
.recording-bar-left {
  display: flex;
  align-items: center;
  gap: 8px;
}
.recording-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #f44336;
  animation: blink-dot 1s ease-in-out infinite;
}
@keyframes blink-dot {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
}
.recording-label {
  font-size: 13px;
  font-weight: 600;
  color: #e65100;
}
.recording-time {
  font-size: 12px;
  font-family: monospace;
  color: #bf360c;
  background: rgba(191, 54, 12, 0.08);
  padding: 1px 6px;
  border-radius: 4px;
}
.recording-bar-right {
  display: flex;
  align-items: center;
  gap: 10px;
}
.countdown-wrapper {
  position: relative;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.countdown-ring {
  transform: rotate(-90deg);
  position: absolute;
  top: 0;
  left: 0;
}
.countdown-arc {
  transition: stroke-dashoffset 0.1s linear;
}
.countdown-num {
  font-size: 11px;
  font-weight: 700;
  color: #f57f17;
  z-index: 1;
}
.done-btn {
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0;
  min-width: 80px;
}
.processing-bar {
  display: flex;
  align-items: center;
  background: #e3f2fd;
  border: 1px solid #90caf9;
  border-radius: 8px;
  padding: 6px 12px;
  margin-top: 4px;
}
.processing-label {
  font-size: 13px;
  color: #1565c0;
  font-weight: 500;
}
.interim-transcript {
  font-style: italic;
  padding: 4px 12px;
  min-height: 24px;
}
</style>
